import { LOCALE_FLAGS, type Locale } from '@/lib/i18n';
import { normalizeCountryCode } from '@/lib/geolocation.shared';

export type LocaleFlagOverrides = Partial<Record<Locale, string>>;

/**
 * Country-specific locale flag overrides (ISO 3166-1 alpha-2).
 * Use when default locale flags (e.g. GB for English) are misleading for a region.
 */
export const LOCALE_FLAG_OVERRIDES_BY_COUNTRY: Record<string, LocaleFlagOverrides> = {
  // English + French
  CA: { en: '🇨🇦', fr: '🇨🇦' },
  BE: { fr: '🇧🇪' },
  CH: { fr: '🇨🇭' },
  LU: { fr: '🇱🇺' },
  CM: { en: '🇨🇲', fr: '🇨🇲' },
  RW: { en: '🇷🇼', fr: '🇷🇼' },
  SC: { en: '🇸🇨', fr: '🇸🇨' },
  VU: { en: '🇻🇺', fr: '🇻🇺' },

  // English
  US: { en: '🇺🇸' },
  GB: { en: '🇬🇧' },
  AU: { en: '🇦🇺' },
  NZ: { en: '🇳🇿' },
  IE: { en: '🇮🇪' },
  ZA: { en: '🇿🇦' },
  IN: { en: '🇮🇳' },
  SG: { en: '🇸🇬' },
  PH: { en: '🇵🇭' },
  NG: { en: '🇳🇬' },
  KE: { en: '🇰🇪' },
  GH: { en: '🇬🇭' },
  JM: { en: '🇯🇲' },
  TT: { en: '🇹🇹' },
  BB: { en: '🇧🇧' },
  BS: { en: '🇧🇸' },
  BZ: { en: '🇧🇿' },
  GY: { en: '🇬🇾' },
  MT: { en: '🇲🇹' },

  // French
  FR: { fr: '🇫🇷' },
  MC: { fr: '🇲🇨' },
  HT: { fr: '🇭🇹' },
  SN: { fr: '🇸🇳' },
  CI: { fr: '🇨🇮' },
  CD: { fr: '🇨🇩' },
  BJ: { fr: '🇧🇯' },
  BF: { fr: '🇧🇫' },
  TG: { fr: '🇹🇬' },
  GA: { fr: '🇬🇦' },
  GN: { fr: '🇬🇳' },
  ML: { fr: '🇲🇱' },
  NE: { fr: '🇳🇪' },
  TD: { fr: '🇹🇩' },
  CF: { fr: '🇨🇫' },
  DJ: { fr: '🇩🇯' },
  MG: { fr: '🇲🇬' },
  KM: { fr: '🇰🇲' },

  // Spanish
  ES: { es: '🇪🇸' },
  MX: { es: '🇲🇽' },
  CO: { es: '🇨🇴' },
  AR: { es: '🇦🇷' },
  PE: { es: '🇵🇪' },
  VE: { es: '🇻🇪' },
  CL: { es: '🇨🇱' },
  EC: { es: '🇪🇨' },
  GT: { es: '🇬🇹' },
  CU: { es: '🇨🇺' },
  BO: { es: '🇧🇴' },
  PY: { es: '🇵🇾' },
  UY: { es: '🇺🇾' },
  CR: { es: '🇨🇷' },
  PA: { es: '🇵🇦' },
  DO: { es: '🇩🇴' },
  HN: { es: '🇭🇳' },
  SV: { es: '🇸🇻' },
  NI: { es: '🇳🇮' },
  GQ: { es: '🇬🇶', fr: '🇬🇶' },

  // Russian
  RU: { ru: '🇷🇺' },
  BY: { ru: '🇧🇾' },
  KZ: { ru: '🇰🇿' },
  KG: { ru: '🇰🇬' },
};

export function getLocaleFlag(locale: Locale, countryCode?: string | null): string {
  const country = normalizeCountryCode(countryCode);
  if (country) {
    const override = LOCALE_FLAG_OVERRIDES_BY_COUNTRY[country]?.[locale];
    if (override) return override;
  }
  return LOCALE_FLAGS[locale];
}
